import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUser, FaIdCard, FaPhone, FaEnvelope, FaCity, FaUsers, FaChild, FaArrowRight, FaArrowLeft, FaWhatsapp } from 'react-icons/fa';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { supabaseUrl, supabaseAnonKey, supabase } from '../lib/supabaseClient';

const FullHouseQuoteModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    dni: '',
    phone: '',
    email: '',
    city: '',
    checkin: '',
    checkout: '',
    guests: '1',
    children: '0',
  });
  const [range, setRange] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Sync range state with formData
  useEffect(() => {
    if (range?.from) {
      setFormData(prev => ({ ...prev, checkin: format(range.from, 'yyyy-MM-dd') }));
    } else {
      setFormData(prev => ({ ...prev, checkin: '' }));
    }
    if (range?.to) {
      setFormData(prev => ({ ...prev, checkout: format(range.to, 'yyyy-MM-dd') }));
    } else {
      setFormData(prev => ({ ...prev, checkout: '' }));
    }
  }, [range]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleClose = () => {
    setSubmissionSuccess(false); // Resetear estado de éxito al cerrar
    setCurrentStep(1); // Resetear a paso 1
    setRange(undefined); // Resetear rango de fechas
    setFormData({ // Resetear formulario
      name: '', dni: '', phone: '', email: '', city: '',
      checkin: '', checkout: '', guests: '1', children: '0',
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('handleSubmit llamado, currentStep:', currentStep, 'isSubmitting:', isSubmitting);
    
    // Only submit if we're on step 3 and not already submitting
    if (currentStep !== 3 || isSubmitting) {
      console.log('Submit bloqueado - paso actual:', currentStep);
      return;
    }
    
    setIsSubmitting(true);

    try {
      console.log('Iniciando envío de formulario...');
      console.log('Datos del formulario:', formData);
      
      // Validar que supabase esté inicializado
      if (!supabase) {
        throw new Error('Error de configuración: Cliente de Supabase no disponible');
      }
      
      console.log('URL de Supabase:', supabaseUrl);
      console.log('Invocando función de Supabase...');
      
      // Usar fetch directamente para mejor compatibilidad
      const functionUrl = `${supabaseUrl}/functions/v1/send-quote-email`;
      console.log('Function URL:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify(formData),
      });

      console.log('Respuesta recibida - Status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error de la función:', errorData);
        
        let errorMsg = 'Error al enviar la solicitud. ';
        if (errorData.error) {
          errorMsg += errorData.error;
        } else {
          errorMsg += `Código de error: ${response.status}`;
        }
        throw new Error(errorMsg);
      }
      
      const data = await response.json();
      console.log('Éxito! Respuesta de la función:', data);
      setSubmissionSuccess(true);
      openWhatsAppWithDetails();

    } catch (error) {
      console.error('--- ERROR DETALLADO EN EL PROCESO DE ENVÍO ---');
      console.error('Tipo de error:', error.constructor.name);
      console.error('Mensaje:', error.message);
      console.error('Stack:', error.stack);
      console.error('Error completo:', error);

      // Mensaje de error más amigable
      let userMessage = 'Hubo un error al enviar tu solicitud.\n\n';
      
      if (error.message) {
        userMessage += error.message;
      } else {
        userMessage += 'Por favor verifica tu conexión a internet e intenta nuevamente.';
      }
      
      userMessage += '\n\nSi el problema persiste, contáctanos directamente por WhatsApp.';
      
      alert(userMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canProceedToNextStep = () => {
    switch(currentStep) {
      case 1:
        return formData.name && formData.dni && formData.phone && formData.email && formData.city;
      case 2:
        return formData.checkin && formData.checkout;
      case 3:
        return formData.guests && parseInt(formData.guests) > 0;
      default:
        return true;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  const openWhatsAppWithDetails = () => {
    const nights = calculateNights();
    const message = [
      `Hola! Estoy interesado en alquilar la *Casa Completa* de Sumaq Mayu:`,
      ``,
      `*Nombre:* ${formData.name}`,
      `*DNI:* ${formData.dni}`,
      `*Celular:* ${formData.phone}`,
      `*Email:* ${formData.email}`,
      `*Ciudad:* ${formData.city}`,
      ``,
      `*Check-in:* ${formatDate(formData.checkin)}`,
      `*Check-out:* ${formatDate(formData.checkout)}`,
      `*Noches:* ${nights}`,
      ``,
      `*Huespedes:* ${formData.guests} adultos, ${formData.children} ninos`,
      ``,
      `Quedo atento a su cotizacion. Gracias!`,
    ].join('\n');

    window.open(`https://wa.me/51947791970?text=${encodeURIComponent(message)}`, '_blank');
  };

  const calculateNights = () => {
    if (!formData.checkin || !formData.checkout) return 0;
    const checkinDate = new Date(formData.checkin);
    const checkoutDate = new Date(formData.checkout);
    const timeDiff = checkoutDate.getTime() - checkinDate.getTime();
    const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return dayDiff > 0 ? dayDiff : 0;
  };

  const renderStepIndicator = () => {
    return (
      <div className="flex justify-center items-center gap-2 mb-6">
        {[1, 2, 3].map(step => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-all ${
              step === currentStep 
                ? 'bg-green-600 text-white scale-110' 
                : step < currentStep 
                  ? 'bg-green-400 text-white' 
                  : 'bg-gray-300 text-gray-600'
            }`}>
              {step}
            </div>
            {step < 3 && <div className={`w-12 h-1 mx-1 ${step < currentStep ? 'bg-green-400' : 'bg-gray-300'}`} />}
          </div>
        ))}
      </div>
    );
  };

  const renderStep = () => {
    const totalNights = calculateNights();

    switch(currentStep) {
      case 1:
        return (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-5"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-4">Paso 1: Datos Personales</h3>
            <div>
              <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-700">Nombre Completo</label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Tu nombre completo" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="dni" className="block mb-2 text-sm font-medium text-gray-700">DNI</label>
              <div className="relative">
                <FaIdCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" id="dni" name="dni" value={formData.dni} onChange={handleChange} placeholder="Tu número de DNI" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-700">Celular</label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="987 654 321" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">Email</label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="tu@email.com" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
            <div>
              <label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-700">Ciudad de Procedencia</label>
              <div className="relative">
                <FaCity className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} placeholder="Tu ciudad" required className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
              </div>
            </div>
          </motion.div>
        );
      
      case 2:
        let footerText = 'Por favor, selecciona el primer día de tu estadía.';
        if (range?.from) {
          if (!range.to) {
            footerText = `Llegada: ${format(range.from, 'd LLL y', { locale: es })}. Ahora selecciona tu salida.`;
          } else if (range.to) {
            const nights = calculateNights();
            footerText = `Total: ${nights} ${nights === 1 ? 'noche' : 'noches'} de estadía.`;
          }
        }

        return (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex flex-col items-center"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-2">Paso 2: Fechas de Estadía</h3>
            <p className="text-gray-500 text-center mb-4 text-sm">{footerText}</p>
            <DayPicker
              id="day-picker"
              mode="range"
              locale={es}
              selected={range}
              onSelect={setRange}
              disabled={{ before: new Date() }}
              className="w-full flex justify-center"
              numberOfMonths={1}
            />
            
            {range?.from && range?.to && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 w-full text-center">
                 <p className="font-semibold text-blue-800">Llegada: {format(range.from, 'PPP', { locale: es })}</p>
                 <p className="font-semibold text-blue-800">Salida: {format(range.to, 'PPP', { locale: es })}</p>
              </div>
            )}
          </motion.div>
        );
      
      case 3:
        return (
          <motion.div
            key="step3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-5"
          >
            <h3 className="text-xl font-playfair font-semibold text-gray-800 mb-4">Paso 3: Información de Huéspedes</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="guests" className="block mb-2 text-sm font-medium text-gray-700">Nº de Huéspedes Adultos</label>
                <div className="relative">
                  <FaUsers className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="number" id="guests" name="guests" value={formData.guests} onChange={handleChange} min="1" className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                </div>
              </div>
              <div>
                <label htmlFor="children" className="block mb-2 text-sm font-medium text-gray-700">Nº de Niños</label>
                <div className="relative">
                  <FaChild className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="number" id="children" name="children" value={formData.children} onChange={handleChange} min="0" className="w-full pl-10 p-3 text-base bg-gray-50 border border-transparent rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                </div>
              </div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 mt-4">
              <h4 className="font-semibold text-gray-800 mb-2">Resumen de tu solicitud:</h4>
              <div className="text-sm text-gray-700 space-y-1">
                <p>• <strong>Total de personas:</strong> {parseInt(formData.guests) + parseInt(formData.children)}</p>
                {totalNights > 0 && <p>• <strong>Noches:</strong> {totalNights}</p>}
                <p className="mt-3 text-xs text-gray-600">Recibirás una cotización personalizada por correo electrónico.</p>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (!isOpen || !isMounted) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="fixed inset-0 bg-black bg-opacity-80 z-[99999] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white overflow-y-auto relative w-screen h-screen rounded-none max-w-none max-h-none sm:rounded-xl sm:max-w-2xl sm:max-h-[90vh] sm:w-full"
          >
            <div className="p-5 sm:p-6 text-center border-b border-gray-200 sticky top-0 bg-green-50 z-10 sm:rounded-t-xl">
              <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 p-2">
                <FaTimes size={20} />
              </button>
              <h2 className="text-2xl sm:text-3xl font-playfair font-bold text-gray-900 mb-2">
                {submissionSuccess ? '¡Solicitud Enviada!' : 'Cotizar Alquiler de Casa Completa'}
              </h2>
              <p className="text-gray-500 text-sm">
                {submissionSuccess ? 'Gracias por tu interés. Nos pondremos en contacto contigo pronto.' : 'Completa el formulario para recibir una cotización personalizada.'}
              </p>
            </div>

            <div className="p-5 sm:p-6">
              <AnimatePresence mode="wait">
                {!submissionSuccess ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {renderStepIndicator()}
                    
                    <form id="full-quote-form">
                      <AnimatePresence mode="wait">
                        {renderStep()}
                      </AnimatePresence>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-8"
                  >
                    <div className="mb-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">¡Excelente!</h3>
                      <p className="text-gray-600 mb-2">Hemos recibido tu solicitud.</p>
                      <p className="text-sm text-gray-500">Nos pondremos en contacto contigo a la brevedad posible con una cotización detallada.</p>
                    </div>
                    <button onClick={openWhatsAppWithDetails} className="w-full bg-[#25D366] text-white p-3 text-lg font-semibold rounded-lg hover:bg-[#20BD5C] transition-colors flex items-center justify-center gap-2 mb-3">
                      <FaWhatsapp size={20} /> Enviar por WhatsApp
                    </button>
                    <button onClick={handleClose} className="w-full bg-blue-600 text-white p-3 text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                      Cerrar
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {!submissionSuccess && (
              <div className="p-5 sm:p-6 bg-green-50 border-t border-gray-200 sm:rounded-b-xl">
                <div className="flex gap-3">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="flex-1 bg-gray-300 text-gray-700 p-3 text-base font-semibold rounded-lg hover:bg-gray-400 transition-all flex items-center justify-center gap-2"
                    >
                      <FaArrowLeft /> Anterior
                    </button>
                  )}
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!canProceedToNextStep()}
                      className="flex-1 bg-green-600 text-white p-3 text-base font-semibold rounded-lg hover:bg-green-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      Siguiente <FaArrowRight />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting || !canProceedToNextStep()}
                      className="flex-1 bg-green-600 text-white p-3 text-base font-semibold rounded-lg hover:bg-green-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  , document.body);
};

export default FullHouseQuoteModal;